import type { Feature, FeatureCollection, LineString } from 'geojson';

import type { DatasetQuery, DatasetRow } from '@/types/api/modules/business-intelligence';

export const GLOBAL_TRADE_DATASET_ID = 'global-trade';
export const GLOBAL_TRADE_VIEW_KIND = 'global-trade-view';
export const GLOBAL_TRADE_VIEW_VERSION = 1;

export const TRADE_MODE_OPTIONS = [
    { label: 'Ocean', value: 'ocean' },
    { label: 'Air', value: 'air' },
    { label: 'Rail', value: 'rail' },
    { label: 'Road', value: 'road' },
] as const;

export type TradeMode = (typeof TRADE_MODE_OPTIONS)[number]['value'];
export type TradeProjection = 'globe' | 'mercator';
export const TRADE_MAP_THEMES = ['adaptive', 'positron', 'dark', 'bright', 'liberty'] as const;
export type TradeMapTheme = (typeof TRADE_MAP_THEMES)[number];
export type TradeRegion = 'Africa' | 'Asia Pacific' | 'Europe' | 'Latin America' | 'Middle East' | 'North America';

export interface TradeDataSource {
    category: string;
    description?: string;
    id: string;
    label: string;
}

export interface TradeHub {
    country: string;
    id: string;
    kind: 'airport' | 'border' | 'inland-port' | 'port';
    latitude: number;
    longitude: number;
    name: string;
    region: TradeRegion;
}

export interface TradeFlow {
    category: string;
    destinationId: string;
    id: string;
    label: string;
    mode: TradeMode;
    originId: string;
    sourceId: string;
}

export interface TradeFilters {
    modes: TradeMode[];
    query: string;
    regions: TradeRegion[];
    sourceIds: string[];
}

export interface TradeMapView {
    bearing?: number;
    city: string;
    latitude: number;
    longitude: number;
    pitch?: number;
    timeZone: string;
    zoom: number;
}

export interface GlobalTradeViewDefinition {
    dataset: {
        id: typeof GLOBAL_TRADE_DATASET_ID;
        sourceIds: string[];
    };
    filters: TradeFilters;
    kind: typeof GLOBAL_TRADE_VIEW_KIND;
    layers: {
        corridors: boolean;
        hubs: boolean;
    };
    presentation: {
        projection: TradeProjection;
        theme?: TradeMapTheme;
    };
    version: typeof GLOBAL_TRADE_VIEW_VERSION;
    viewport: Pick<TradeMapView, 'bearing' | 'latitude' | 'longitude' | 'pitch' | 'zoom'>;
}

const GLOBAL_TRADE_QUERY_DIMENSIONS = [
    'observationId',
    'label',
    'sourceId',
    'categoryId',
    'classification',
    'entityId',
    'originId',
    'originName',
    'originCountry',
    'originRegion',
    'originKind',
    'originLatitude',
    'originLongitude',
    'destinationId',
    'destinationName',
    'destinationCountry',
    'destinationRegion',
    'destinationKind',
    'destinationLatitude',
    'destinationLongitude',
    'mode',
    'unit',
    'observedAt',
] as const;

export const createGlobalTradeDatasetQuery = (view: GlobalTradeViewDefinition): DatasetQuery => ({
    datasetId: GLOBAL_TRADE_DATASET_ID,
    dimensions: GLOBAL_TRADE_QUERY_DIMENSIONS.map((field) => ({ field })),
    filters: {
        condition: 'AND',
        rules: [
            ...(view.dataset.sourceIds.length
                ? [{ field: 'sourceId', operator: 'in' as const, values: view.dataset.sourceIds }]
                : []),
            ...(view.filters.modes.length
                ? [{ field: 'mode', operator: 'in' as const, values: view.filters.modes }]
                : []),
            ...(view.filters.regions.length
                ? [
                      { field: 'originRegion', operator: 'in' as const, values: view.filters.regions },
                      { field: 'destinationRegion', operator: 'in' as const, values: view.filters.regions },
                  ]
                : []),
        ],
    },
    limit: 10_000,
    metrics: [{ aggregation: 'sum', field: 'value', label: 'Observed Value' }],
});

const stringValue = (value: DatasetRow[string]) => (typeof value === 'string' ? value : undefined);
const numberValue = (value: DatasetRow[string]) => (typeof value === 'number' ? value : undefined);
const tradeModes = new Set<string>(TRADE_MODE_OPTIONS.map((option) => option.value));
const tradeRegions = new Set<string>([
    'Africa',
    'Asia Pacific',
    'Europe',
    'Latin America',
    'Middle East',
    'North America',
]);
const hubKinds = new Set<string>(['airport', 'border', 'inland-port', 'port']);

export const globalTradeRowsToMapData = (rows: DatasetRow[]) => {
    const hubs = new Map<string, TradeHub>();
    const flows: TradeFlow[] = [];

    rows.forEach((row) => {
        const id = stringValue(row.observationId) ?? stringValue(row.entityId);
        const sourceId = stringValue(row.sourceId);
        const originId = stringValue(row.originId);
        const destinationId = stringValue(row.destinationId);
        const mode = stringValue(row.mode);
        const originLatitude = numberValue(row.originLatitude);
        const originLongitude = numberValue(row.originLongitude);
        const destinationLatitude = numberValue(row.destinationLatitude);
        const destinationLongitude = numberValue(row.destinationLongitude);
        const originRegion = stringValue(row.originRegion);
        const destinationRegion = stringValue(row.destinationRegion);
        const originKind = stringValue(row.originKind);
        const destinationKind = stringValue(row.destinationKind);

        if (
            !id ||
            !sourceId ||
            !originId ||
            !destinationId ||
            !mode ||
            !tradeModes.has(mode) ||
            originLatitude === undefined ||
            originLongitude === undefined ||
            destinationLatitude === undefined ||
            destinationLongitude === undefined ||
            !originRegion ||
            !tradeRegions.has(originRegion) ||
            !destinationRegion ||
            !tradeRegions.has(destinationRegion) ||
            !originKind ||
            !hubKinds.has(originKind) ||
            !destinationKind ||
            !hubKinds.has(destinationKind)
        ) {
            return;
        }

        hubs.set(originId, {
            country: stringValue(row.originCountry) ?? '',
            id: originId,
            kind: originKind as TradeHub['kind'],
            latitude: originLatitude,
            longitude: originLongitude,
            name: stringValue(row.originName) ?? originId,
            region: originRegion as TradeRegion,
        });
        hubs.set(destinationId, {
            country: stringValue(row.destinationCountry) ?? '',
            id: destinationId,
            kind: destinationKind as TradeHub['kind'],
            latitude: destinationLatitude,
            longitude: destinationLongitude,
            name: stringValue(row.destinationName) ?? destinationId,
            region: destinationRegion as TradeRegion,
        });
        flows.push({
            category: stringValue(row.classification) ?? stringValue(row.categoryId) ?? 'Uncategorized',
            destinationId,
            id,
            label: stringValue(row.label) ?? `${originId} → ${destinationId}`,
            mode: mode as TradeMode,
            originId,
            sourceId,
        });
    });

    return { flows, hubs: Array.from(hubs.values()) };
};

export interface TradeFlowFeatureProperties {
    category: string;
    id: string;
    label: string;
    mode: TradeMode;
}

export const TRADE_REGIONS: TradeRegion[] = [
    'Africa',
    'Asia Pacific',
    'Europe',
    'Latin America',
    'Middle East',
    'North America',
];

export const DEFAULT_TRADE_FILTERS: TradeFilters = {
    modes: TRADE_MODE_OPTIONS.map((option) => option.value),
    query: '',
    regions: TRADE_REGIONS,
    sourceIds: [],
};

export const DEFAULT_TRADE_MAP_VIEW: TradeMapView = {
    city: 'Chicago',
    latitude: 41.8781,
    longitude: -87.6298,
    timeZone: 'America/Chicago',
    zoom: 2.35,
};

const TRADE_TIMEZONE_MAP_VIEWS: Record<string, TradeMapView> = {
    'America/Chicago': DEFAULT_TRADE_MAP_VIEW,
    'America/Los_Angeles': {
        city: 'Los Angeles',
        latitude: 34.0522,
        longitude: -118.2437,
        timeZone: 'America/Los_Angeles',
        zoom: 2.35,
    },
    'America/New_York': {
        city: 'New York',
        latitude: 40.7128,
        longitude: -74.006,
        timeZone: 'America/New_York',
        zoom: 2.35,
    },
    'Asia/Dubai': { city: 'Dubai', latitude: 25.2048, longitude: 55.2708, timeZone: 'Asia/Dubai', zoom: 2.35 },
    'Asia/Hong_Kong': {
        city: 'Hong Kong',
        latitude: 22.3193,
        longitude: 114.1694,
        timeZone: 'Asia/Hong_Kong',
        zoom: 2.35,
    },
    'Asia/Seoul': { city: 'Seoul', latitude: 37.5665, longitude: 126.978, timeZone: 'Asia/Seoul', zoom: 2.35 },
    'Asia/Shanghai': {
        city: 'Shanghai',
        latitude: 31.2304,
        longitude: 121.4737,
        timeZone: 'Asia/Shanghai',
        zoom: 2.35,
    },
    'Asia/Singapore': {
        city: 'Singapore',
        latitude: 1.3521,
        longitude: 103.8198,
        timeZone: 'Asia/Singapore',
        zoom: 2.35,
    },
    'Asia/Tokyo': { city: 'Tokyo', latitude: 35.6762, longitude: 139.6503, timeZone: 'Asia/Tokyo', zoom: 2.35 },
    'Europe/Berlin': {
        city: 'Frankfurt',
        latitude: 50.1109,
        longitude: 8.6821,
        timeZone: 'Europe/Berlin',
        zoom: 2.35,
    },
    'Europe/Dublin': {
        city: 'Dublin',
        latitude: 53.3498,
        longitude: -6.2603,
        timeZone: 'Europe/Dublin',
        zoom: 2.35,
    },
    'Europe/London': {
        city: 'London',
        latitude: 51.5072,
        longitude: -0.1276,
        timeZone: 'Europe/London',
        zoom: 2.35,
    },
    'Europe/Paris': { city: 'Paris', latitude: 48.8566, longitude: 2.3522, timeZone: 'Europe/Paris', zoom: 2.35 },
    'Europe/Zurich': {
        city: 'Geneva',
        latitude: 46.2044,
        longitude: 6.1432,
        timeZone: 'Europe/Zurich',
        zoom: 2.35,
    },
};

export const getTradeMapView = (timeZone?: string): TradeMapView =>
    (timeZone ? TRADE_TIMEZONE_MAP_VIEWS[timeZone] : undefined) ?? DEFAULT_TRADE_MAP_VIEW;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
const toDegrees = (radians: number) => (radians * 180) / Math.PI;

export const buildGreatCircleCoordinates = (
    origin: Pick<TradeHub, 'latitude' | 'longitude'>,
    destination: Pick<TradeHub, 'latitude' | 'longitude'>,
    segmentCount = 48,
): [number, number][] => {
    const originLatitude = toRadians(origin.latitude);
    const originLongitude = toRadians(origin.longitude);
    const destinationLatitude = toRadians(destination.latitude);
    const destinationLongitude = toRadians(destination.longitude);
    const originVector = [
        Math.cos(originLatitude) * Math.cos(originLongitude),
        Math.cos(originLatitude) * Math.sin(originLongitude),
        Math.sin(originLatitude),
    ];
    const destinationVector = [
        Math.cos(destinationLatitude) * Math.cos(destinationLongitude),
        Math.cos(destinationLatitude) * Math.sin(destinationLongitude),
        Math.sin(destinationLatitude),
    ];
    const dotProduct = Math.max(
        -1,
        Math.min(
            1,
            originVector.reduce((sum, value, index) => sum + value * destinationVector[index], 0),
        ),
    );
    const angle = Math.acos(dotProduct);
    let previousLongitude = origin.longitude;

    return Array.from({ length: Math.max(2, segmentCount) + 1 }, (_, index) => {
        const progress = index / Math.max(2, segmentCount);
        const sinAngle = Math.sin(angle);
        const originWeight = sinAngle ? Math.sin((1 - progress) * angle) / sinAngle : 1 - progress;
        const destinationWeight = sinAngle ? Math.sin(progress * angle) / sinAngle : progress;
        const x = originWeight * originVector[0] + destinationWeight * destinationVector[0];
        const y = originWeight * originVector[1] + destinationWeight * destinationVector[1];
        const z = originWeight * originVector[2] + destinationWeight * destinationVector[2];
        const latitude = toDegrees(Math.atan2(z, Math.sqrt(x * x + y * y)));
        let longitude = toDegrees(Math.atan2(y, x));

        while (longitude - previousLongitude > 180) {
            longitude -= 360;
        }
        while (longitude - previousLongitude < -180) {
            longitude += 360;
        }
        previousLongitude = longitude;

        return [longitude, latitude];
    });
};

export const filterTradeFlows = (
    flows: TradeFlow[],
    hubs: TradeHub[],
    filters: TradeFilters,
    filterJoin: 'all' | 'any' = 'all',
    matchMode: 'partial' | 'exact' = 'partial',
) => {
    const hubById = new Map(hubs.map((hub) => [hub.id, hub]));
    const normalizedQuery = filters.query.trim().toLowerCase();

    return flows.filter((flow) => {
        const origin = hubById.get(flow.originId);
        const destination = hubById.get(flow.destinationId);

        if (!origin || !destination) {
            return false;
        }

        const searchableValues = [
            flow.label,
            flow.category,
            flow.mode,
            origin?.name,
            origin?.country,
            destination?.name,
            destination?.country,
        ].filter(Boolean);
        const matchesQuery =
            !normalizedQuery ||
            searchableValues.some((value) => {
                const normalizedValue = String(value).trim().toLowerCase();

                return matchMode === 'exact'
                    ? normalizedValue === normalizedQuery
                    : normalizedValue.includes(normalizedQuery);
            });

        const activePredicates = [
            filters.modes.length !== DEFAULT_TRADE_FILTERS.modes.length ? filters.modes.includes(flow.mode) : undefined,
            filters.sourceIds.length ? filters.sourceIds.includes(flow.sourceId) : undefined,
            filters.regions.length !== DEFAULT_TRADE_FILTERS.regions.length
                ? filters.regions.includes(origin.region) || filters.regions.includes(destination.region)
                : undefined,
        ].filter((predicate): predicate is boolean => predicate !== undefined);

        return (
            matchesQuery &&
            (activePredicates.length === 0 ||
                (filterJoin === 'all' ? activePredicates.every(Boolean) : activePredicates.some(Boolean)))
        );
    });
};

export const createTradeFlowFeatureCollection = (
    flows: TradeFlow[],
    hubs: TradeHub[],
): FeatureCollection<LineString, TradeFlowFeatureProperties> => {
    const hubById = new Map(hubs.map((hub) => [hub.id, hub]));
    const features = flows.reduce<Feature<LineString, TradeFlowFeatureProperties>[]>((result, flow) => {
        const origin = hubById.get(flow.originId);
        const destination = hubById.get(flow.destinationId);

        if (!origin || !destination) {
            return result;
        }

        result.push({
            geometry: {
                coordinates: buildGreatCircleCoordinates(origin, destination),
                type: 'LineString',
            },
            properties: {
                category: flow.category,
                id: flow.id,
                label: flow.label,
                mode: flow.mode,
            },
            type: 'Feature',
        });

        return result;
    }, []);

    return { features, type: 'FeatureCollection' };
};

export const getVisibleTradeHubs = (flows: TradeFlow[], hubs: TradeHub[]) => {
    const visibleHubIds = new Set(flows.flatMap((flow) => [flow.originId, flow.destinationId]));

    return hubs.filter((hub) => visibleHubIds.has(hub.id));
};

export const countActiveTradeFilters = (filters: TradeFilters) =>
    Number(Boolean(filters.query.trim())) +
    Number(filters.modes.length !== DEFAULT_TRADE_FILTERS.modes.length) +
    Number(filters.regions.length !== DEFAULT_TRADE_FILTERS.regions.length) +
    Number(filters.sourceIds.length !== DEFAULT_TRADE_FILTERS.sourceIds.length);

const hasOnlyAllowedValues = <T extends string>(values: unknown, allowed: readonly T[]): values is T[] =>
    Array.isArray(values) && values.every((value) => typeof value === 'string' && allowed.includes(value as T));

export const createGlobalTradeViewDefinition = (input: {
    filters: TradeFilters;
    mapView: Pick<TradeMapView, 'bearing' | 'latitude' | 'longitude' | 'pitch' | 'zoom'>;
    projection: TradeProjection;
    theme?: TradeMapTheme;
    showFlows: boolean;
    showHubs: boolean;
}): GlobalTradeViewDefinition => ({
    dataset: {
        id: GLOBAL_TRADE_DATASET_ID,
        sourceIds: [...input.filters.sourceIds],
    },
    filters: {
        modes: [...input.filters.modes],
        query: input.filters.query.trim(),
        regions: [...input.filters.regions],
        sourceIds: [...input.filters.sourceIds],
    },
    kind: GLOBAL_TRADE_VIEW_KIND,
    layers: {
        corridors: input.showFlows,
        hubs: input.showHubs,
    },
    presentation: {
        projection: input.projection,
        theme: input.theme ?? 'adaptive',
    },
    version: GLOBAL_TRADE_VIEW_VERSION,
    viewport: {
        bearing: input.mapView.bearing ?? 0,
        latitude: input.mapView.latitude,
        longitude: input.mapView.longitude,
        pitch: input.mapView.pitch ?? 0,
        zoom: input.mapView.zoom,
    },
});

export const isGlobalTradeViewDefinition = (value: unknown): value is GlobalTradeViewDefinition => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const definition = value as Partial<GlobalTradeViewDefinition>;
    const filters = definition.filters;
    const viewport = definition.viewport;
    const hasValidSourceIds = (sourceIds: unknown) =>
        Array.isArray(sourceIds) &&
        sourceIds.length <= 100 &&
        sourceIds.every((sourceId) => typeof sourceId === 'string' && sourceId.length > 0 && sourceId.length <= 100);

    return (
        definition.kind === GLOBAL_TRADE_VIEW_KIND &&
        definition.version === GLOBAL_TRADE_VIEW_VERSION &&
        definition.dataset?.id === GLOBAL_TRADE_DATASET_ID &&
        hasValidSourceIds(definition.dataset?.sourceIds) &&
        Boolean(filters) &&
        typeof filters?.query === 'string' &&
        filters.query.length <= 250 &&
        hasOnlyAllowedValues(
            filters.modes,
            TRADE_MODE_OPTIONS.map((option) => option.value),
        ) &&
        hasOnlyAllowedValues(filters.regions, TRADE_REGIONS) &&
        hasValidSourceIds(filters.sourceIds) &&
        typeof definition.layers?.corridors === 'boolean' &&
        typeof definition.layers?.hubs === 'boolean' &&
        (definition.presentation?.projection === 'globe' || definition.presentation?.projection === 'mercator') &&
        (definition.presentation.theme === undefined ||
            TRADE_MAP_THEMES.includes(definition.presentation.theme as TradeMapTheme)) &&
        Boolean(viewport) &&
        typeof viewport?.latitude === 'number' &&
        viewport.latitude >= -90 &&
        viewport.latitude <= 90 &&
        typeof viewport.longitude === 'number' &&
        viewport.longitude >= -180 &&
        viewport.longitude <= 180 &&
        typeof viewport.zoom === 'number' &&
        viewport.zoom >= 0 &&
        viewport.zoom <= 24
    );
};
